/**
 * Field Configuration System
 * Centralized configuration for all admin form fields
 * Migrated from Alpine.js field-config.js
 */

// Field type definitions
export enum FieldType {
  TEXT = 'text',
  TEXTAREA = 'textarea',
  NUMBER = 'number',
  SELECT = 'select',
  CHECKBOX = 'checkbox',
  MARKDOWN = 'markdown',
  JSON = 'json',
}

// Validation rule types
export enum ValidationRuleType {
  REQUIRED = 'required',
  MIN = 'min',
  MAX = 'max',
  MIN_LENGTH = 'minLength',
  MAX_LENGTH = 'maxLength',
  PATTERN = 'pattern',
  CUSTOM = 'custom',
  IN_OPTIONS = 'inOptions',
}

export interface ValidationRule {
  rule: ValidationRuleType;
  value?: any;
  message?: string;
  pattern?: RegExp;
  options?: string[];
  getOptions?: (appContext: any) => string[];
  validate?: (value: any, appContext?: any) => ValidationResult;
}

export interface ValidationResult {
  valid: boolean;
  isWarning?: boolean;
  message?: string;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface FieldSchema {
  type: FieldType;
  label: string | (() => string);
  section: string;
  required?: boolean;
  default?: any;
  placeholder?: string | (() => string);
  help?: string | (() => string) | Record<string, string>;
  readonly?: boolean;

  // Select field options
  options?: SelectOption[];
  optionsSource?: string; // Dynamic options from app context
  getOptionsSource?: (config: any) => string; // Dynamic options source based on form state
  optionsFromConfig?: string; // Dynamic options from current config (e.g., 'output_schemas')
  optionsMapper?: (item: any) => SelectOption; // Map config items to SelectOption

  // Number field constraints
  min?: number;
  max?: number;
  step?: number;

  // Textarea/Markdown rows
  rows?: number;

  // Conditional display
  showIf?: (config: any) => boolean;

  // Validation rules
  validation?: ValidationRule[];
  invalidMessage?: (value: any) => string;

  // JSON field specific
  displayMode?: 'list' | 'textarea';
  hasToolBrowser?: boolean;

  // Special buttons
  hasResetButton?: boolean;
}

export type FieldConfig = Record<string, FieldSchema>;

/**
 * Helper function to get help text from schema
 * Handles string, function, or Record<string, string> (for select fields)
 */
export function getHelpText(
  help: string | (() => string) | Record<string, string> | undefined,
  currentValue?: string
): string | undefined {
  if (!help) return undefined;
  if (typeof help === 'function') return help();
  if (typeof help === 'string') return help;
  // For Record<string, string>, return help for current value or first value
  if (typeof help === 'object') {
    if (currentValue && help[currentValue]) {
      return help[currentValue];
    }
    // Return first help text as fallback
    const firstKey = Object.keys(help)[0];
    return firstKey ? help[firstKey] : undefined;
  }
  return undefined;
}

/**
 * Helper function to get nested config value using dot notation
 */
export function getConfigValue(obj: any, path: string): any {
  if (!path || typeof path !== 'string') return undefined;
  if (!path.includes('.')) return obj?.[path];
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Helper function to validate custom_tools configuration
 */
export function validateToolsConfig(parsed: any): ValidationResult {
  if (typeof parsed === 'string') {
    return { valid: true };
  }

  if (Array.isArray(parsed)) {
    const allStrings = parsed.every((item) => typeof item === 'string');
    if (!allStrings) {
      return {
        valid: false,
        isWarning: true,
        message: 'Array must contain only module name strings',
      };
    }
    return { valid: true };
  }

  if (typeof parsed === 'object' && parsed !== null) {
    if (!parsed.modules || !Array.isArray(parsed.modules)) {
      return {
        valid: false,
        isWarning: true,
        message: 'Object format must have "modules" array field',
      };
    }
    return { valid: true };
  }

  return {
    valid: false,
    isWarning: true,
    message: 'Must be a string, array, or object',
  };
}

/**
 * Helper function to validate mcp_servers configuration
 */
export function validateMcpServersConfig(parsed: any): ValidationResult {
  if (typeof parsed === 'string') {
    return { valid: true };
  }

  if (Array.isArray(parsed)) {
    const allStrings = parsed.every((item) => typeof item === 'string');
    if (!allStrings) {
      return {
        valid: false,
        isWarning: true,
        message: 'Array must contain only server ID strings',
      };
    }
    return { valid: true };
  }

  if (typeof parsed === 'object' && parsed !== null) {
    if (!parsed.servers || !Array.isArray(parsed.servers)) {
      return {
        valid: false,
        isWarning: true,
        message: 'Object format must have "servers" array field',
      };
    }
    return { valid: true };
  }

  return {
    valid: false,
    isWarning: true,
    message: 'Must be a string, array, or object',
  };
}

/**
 * Helper function to validate team_members configuration
 */
export function validateTeamMembersConfig(parsed: any): ValidationResult {
  if (typeof parsed === 'string') {
    return { valid: true };
  }

  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      if (typeof item === 'string') continue;
      if (typeof item === 'object' && item !== null) {
        if (!item.agent_type && !item.type) {
          return {
            valid: false,
            isWarning: true,
            message: 'Member object must have "agent_type" field',
          };
        }
        continue;
      }
      return {
        valid: false,
        isWarning: true,
        message: 'Array items must be strings or member config objects',
      };
    }
    return { valid: true };
  }

  if (typeof parsed === 'object' && parsed !== null) {
    if (!parsed.members || !Array.isArray(parsed.members)) {
      return {
        valid: false,
        isWarning: true,
        message: 'Object format must have "members" array field',
      };
    }
    return validateTeamMembersConfig(parsed.members);
  }

  return {
    valid: false,
    isWarning: true,
    message: 'Must be a string, array, or object with members',
  };
}

/**
 * Main field configuration
 * All field definitions for the agent configuration form
 */
export const FIELD_CONFIG: FieldConfig = {
  // ========== Basic Information ==========
  agent_type: {
    type: FieldType.TEXT,
    label: 'Agent Type',
    help: 'Unique identifier for this agent type',
    section: 'basic',
    readonly: true,
    required: true,
    default: '',
  },

  name: {
    type: FieldType.TEXT,
    label: 'Name',
    placeholder: 'Enter agent name',
    help: 'Display name for the agent',
    section: 'basic',
    required: true,
    default: '',
    validation: [
      { rule: ValidationRuleType.REQUIRED },
      { rule: ValidationRuleType.MIN_LENGTH, value: 1 },
      { rule: ValidationRuleType.MAX_LENGTH, value: 100 },
    ],
  },

  description: {
    type: FieldType.TEXTAREA,
    label: 'Description',
    placeholder: 'Enter agent description',
    help: 'Brief description of the agent\'s purpose and capabilities',
    section: 'basic',
    rows: 3,
    default: '',
    validation: [
      { rule: ValidationRuleType.MAX_LENGTH, value: 500 },
    ],
  },

  // ========== Model Configuration ==========
  multimodal_enabled: {
    type: FieldType.CHECKBOX,
    label: 'Enable Multimodal (Images)',
    help: 'Allow users to send images in chat. When enabled, only vision-capable models will be shown.',
    section: 'models',
    default: false,
  },

  chat_model_id: {
    type: FieldType.SELECT,
    label: 'Chat Model',
    help: 'LLM model for conversation. When multimodal is enabled, only vision-capable models are shown.',
    section: 'models',
    required: true,
    // Dynamically switch between chatModels and visionModels based on multimodal_enabled
    getOptionsSource: (config) => {
      // If multimodal is enabled and there are vision models, show only vision models
      if (config?.multimodal_enabled && config?.visionModels?.length > 0) {
        return 'visionModels';
      }
      return 'chatModels';
    },
    default: 'gpt-4o',
    validation: [
      { rule: ValidationRuleType.REQUIRED },
      {
        rule: ValidationRuleType.IN_OPTIONS,
        // Validate against all chat models (including vision models)
        getOptions: (app) => app.chatModels?.map((m: any) => m.id) || [],
      },
    ],
    invalidMessage: (value) => `Warning: Model '${value}' is not available. It may have been removed.`,
  },

  embedding_model_id: {
    type: FieldType.SELECT,
    label: 'Embedding Model',
    help: 'Model for generating embeddings (knowledge base, memory)',
    section: 'models',
    optionsSource: 'embeddingModels',
    default: 'text-embedding-3-small',
    showIf: (config) => config.knowledge_enabled || getConfigValue(config, 'memory.enabled'),
    validation: [
      {
        rule: ValidationRuleType.IN_OPTIONS,
        getOptions: (app) => app.embeddingModels?.map((m: any) => m.id) || [],
      },
    ],
    invalidMessage: (value) => `Warning: Model '${value}' is not available. It may have been removed.`,
  },

  // ========== Reasoning/Thinking Configuration ==========
  'reasoning.mode': {
    type: FieldType.SELECT,
    label: 'Reasoning Mode',
    help: {
      disabled: 'No reasoning/thinking capabilities enabled.',
      native: 'Use when Chat Model is a native reasoning model (e.g., gpt-5, Claude 4.5 Sonnet, DeepSeek-R1). The model thinks before responding.',
      combined: 'Use a separate Reasoning Model for thinking, while Chat Model generates the final response. Best for complex analysis tasks.',
    },
    section: 'reasoning',
    options: [
      { value: 'disabled', label: 'Disabled' },
      { value: 'native', label: 'Native (Chat Model thinks)' },
      { value: 'combined', label: 'Combined (Separate Reasoning Model)' },
    ],
    default: 'disabled',
  },

  'reasoning.model_id': {
    type: FieldType.SELECT,
    label: 'Reasoning Model',
    help: 'Model for complex thinking tasks. This model handles reasoning while the main Chat Model generates the final response.',
    section: 'reasoning',
    optionsSource: 'reasoningModels',
    default: null,
    showIf: (config) => getConfigValue(config, 'reasoning.mode') === 'combined',
    validation: [
      {
        rule: ValidationRuleType.IN_OPTIONS,
        getOptions: (app) => app.reasoningModels?.map((m: any) => m.id) || [],
      },
    ],
    invalidMessage: (value) => `Warning: Model '${value}' is not available.`,
  },

  'reasoning.budget_tokens': {
    type: FieldType.SELECT,
    label: 'Thinking Depth',
    help: {
      '': 'Auto: Let the model decide based on task complexity.',
      '1024': 'Quick: Fast responses for simple questions (~1K tokens).',
      '4096': 'Balanced: Good for most tasks (~4K tokens).',
      '8192': 'Deep: Thorough analysis for complex problems (~8K tokens).',
      '16384': 'Maximum: Exhaustive reasoning for difficult tasks (~16K tokens).',
    },
    section: 'reasoning',
    options: [
      { value: '', label: 'Auto (Recommended)' },
      { value: '1024', label: 'Quick (~1K tokens)' },
      { value: '4096', label: 'Balanced (~4K tokens)' },
      { value: '8192', label: 'Deep (~8K tokens)' },
      { value: '16384', label: 'Maximum (~16K tokens)' },
    ],
    default: '',
    showIf: (config) => getConfigValue(config, 'reasoning.mode') !== 'disabled',
  },

  // ========== LLM Parameters ==========
  temperature: {
    type: FieldType.NUMBER,
    label: 'Temperature',
    help: 'Sampling temperature (0 = deterministic, 2 = very creative)',
    section: 'llm_parameters',
    min: 0.0,
    max: 2.0,
    step: 0.1,
    default: 0.7,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.0 },
      { rule: ValidationRuleType.MAX, value: 2.0 },
    ],
  },

  top_p: {
    type: FieldType.NUMBER,
    label: 'Top P',
    help: 'Nucleus sampling threshold (0.1 = conservative, 1.0 = diverse)',
    section: 'llm_parameters',
    min: 0.0,
    max: 1.0,
    step: 0.1,
    default: 1.0,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.0 },
      { rule: ValidationRuleType.MAX, value: 1.0 },
    ],
  },

  max_tokens: {
    type: FieldType.NUMBER,
    label: 'Max Tokens',
    help: 'Maximum tokens in response (null = unlimited)',
    section: 'llm_parameters',
    min: 1,
    max: 32000,
    step: 1,
    default: null,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 32000 },
    ],
  },

  // ========== Output Format ==========
  output_format: {
    type: FieldType.SELECT,
    label: 'Output Format',
    help: 'Response output format: default (plain text), markdown, or JSON',
    section: 'output_format',
    default: 'default',
    options: [
      { value: 'default', label: 'Default' },
      { value: 'markdown', label: 'Markdown' },
      { value: 'json', label: 'JSON' },
    ],
  },

  // Note: default_output_schema_key removed - use is_default on OutputSchemaConfig instead

  // ========== Features ==========
  debug_mode: {
    type: FieldType.CHECKBOX,
    label: 'Debug Mode',
    help: 'Show detailed execution information in responses',
    section: 'features',
    default: false,
  },

  storage_enabled: {
    type: FieldType.CHECKBOX,
    label: 'Enable Session Storage',
    help: 'Store chat sessions in database (required for session history)',
    section: 'features',
    default: true,
  },

  knowledge_enabled: {
    type: FieldType.CHECKBOX,
    label: 'Enable Knowledge Base',
    help: 'Use vector knowledge base for context-aware responses',
    section: 'features',
    default: false,
  },

  'hooks.astrology_plus.enabled': {
    type: FieldType.CHECKBOX,
    label: 'Enable Birth Chart Context',
    help: 'Include user birth chart data in prompts (astrology agents)',
    section: 'features',
    default: false,
  },

  'hooks.astrology_plus.include_natal_chart': {
    type: FieldType.CHECKBOX,
    label: 'Include Natal Chart Calculation',
    help: 'Include full natal chart in compact LLM-friendly format (~300-400 bytes). Uses astrological symbols: ☉♎26°H6 ☽♎0°H5. Calculates planets, aspects, and patterns using astro-tool.',
    section: 'features',
    default: false,
    showIf: (config) => getConfigValue(config, 'hooks.astrology_plus.enabled') === true,
  },

  add_datetime_to_instructions: {
    type: FieldType.CHECKBOX,
    label: 'Add Current DateTime',
    help: 'Include current date/time in system instructions',
    section: 'features',
    default: false,
  },

  // ========== Message Processing ==========
  add_history_to_context: {
    type: FieldType.CHECKBOX,
    label: 'Add History to Context',
    help: 'Include previous session runs in context',
    section: 'message_processing',
    default: false,
  },

  num_history_runs: {
    type: FieldType.NUMBER,
    label: 'Number of History Runs',
    help: 'How many previous runs to include',
    section: 'message_processing',
    min: 1,
    max: 20,
    default: 3,
    showIf: (config) => config.add_history_to_context === true,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 20 },
    ],
  },

  read_chat_history: {
    type: FieldType.CHECKBOX,
    label: 'Read Chat History',
    help: 'Include full chat history in context',
    section: 'message_processing',
    default: false,
  },

  // ========== Session Summary Configuration ==========
  'session_summary.enabled': {
    type: FieldType.CHECKBOX,
    label: 'Enable Session Summary',
    help: 'Enable automatic session summary generation. Summaries provide compressed conversation history for long conversations.',
    section: 'session_summary',
    default: false,
  },

  'session_summary.model_id': {
    type: FieldType.SELECT,
    label: 'Summary Model',
    help: 'Model used for summary generation. Use a cost-effective model.',
    section: 'session_summary',
    default: 'gpt-5-mini',
    optionsSource: 'memoryModels',
    showIf: (config) => getConfigValue(config, 'session_summary.enabled') === true,
    validation: [
      {
        rule: ValidationRuleType.IN_OPTIONS,
        getOptions: (app) => app.memoryModels?.map((m: any) => m.id) || [],
      },
    ],
    invalidMessage: (value) => `Warning: Model '${value}' is not available.`,
  },

  'session_summary.summary_frequency': {
    type: FieldType.SELECT,
    label: 'Summary Frequency',
    help: {
      'on_request': 'Generate summary only when explicitly requested via API',
      'after_n_messages': 'Generate summary automatically after N messages (recommended)',
      'end_of_session': 'Generate summary at the end of each session (manual trigger)',
    },
    section: 'session_summary',
    options: [
      { value: 'on_request', label: 'On Request' },
      { value: 'after_n_messages', label: 'After N Messages' },
      { value: 'end_of_session', label: 'End of Session' },
    ],
    default: 'on_request',
    showIf: (config) => getConfigValue(config, 'session_summary.enabled') === true,
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ['on_request', 'after_n_messages', 'end_of_session'] },
    ],
  },

  'session_summary.message_threshold': {
    type: FieldType.NUMBER,
    label: 'Message Threshold',
    help: 'Number of messages before first summary generation. Recommended: Set equal to num_history_runs to avoid information gaps.',
    section: 'session_summary',
    min: 1,
    max: 50,
    step: 1,
    default: 10,
    showIf: (config) =>
      getConfigValue(config, 'session_summary.enabled') === true &&
      getConfigValue(config, 'session_summary.summary_frequency') === 'after_n_messages',
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 50 },
    ],
  },

  'session_summary.regenerate_interval': {
    type: FieldType.NUMBER,
    label: 'Regenerate Interval',
    help: 'How often to regenerate summary after initial generation. 0 = never regenerate. Recommended: Set equal to num_history_runs for rolling coverage.',
    section: 'session_summary',
    min: 0,
    max: 50,
    step: 1,
    default: 0,
    showIf: (config) =>
      getConfigValue(config, 'session_summary.enabled') === true &&
      getConfigValue(config, 'session_summary.summary_frequency') === 'after_n_messages',
    validation: [
      { rule: ValidationRuleType.MIN, value: 0 },
      { rule: ValidationRuleType.MAX, value: 50 },
    ],
  },

  'session_summary.inject_into_prompt': {
    type: FieldType.CHECKBOX,
    label: 'Inject into Prompt',
    help: 'Inject summary into system prompt as dependency data for context-aware responses.',
    section: 'session_summary',
    default: true,
    showIf: (config) => getConfigValue(config, 'session_summary.enabled') === true,
  },

  'session_summary.custom_prompt': {
    type: FieldType.TEXTAREA,
    label: 'Custom Summary Prompt',
    help: 'Custom prompt template for summary generation. Leave empty to use default Agno prompt.',
    placeholder: 'Enter custom prompt for summary generation...',
    section: 'session_summary',
    rows: 6,
    default: null,
    showIf: (config) => getConfigValue(config, 'session_summary.enabled') === true,
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          if (value.length > 2000) {
            return { valid: false, message: 'Custom prompt cannot exceed 2000 characters' };
          }
          return { valid: true };
        },
      },
    ],
  },

  // ========== Advanced Settings ==========
  session_timeout_minutes: {
    type: FieldType.NUMBER,
    label: 'Session Timeout (minutes)',
    help: 'Session expires after this many minutes of inactivity',
    section: 'advanced',
    min: 1,
    max: 1440,
    default: 60,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 1440 },
    ],
  },

  max_cached_resources: {
    type: FieldType.NUMBER,
    label: 'Max Cached Resources',
    help: 'Maximum number of resources to cache per user',
    section: 'advanced',
    min: 1,
    max: 100,
    default: 10,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 100 },
    ],
  },

  resource_ttl_minutes: {
    type: FieldType.NUMBER,
    label: 'Resource TTL (minutes)',
    help: 'How long to cache resources before expiring',
    section: 'advanced',
    min: 1,
    max: 1440,
    default: 30,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 1440 },
    ],
  },

  // ========== Custom Tools ==========
  custom_tools: {
    type: FieldType.JSON,
    label: 'Custom Tools Configuration',
    help: 'Configure custom tool modules for this agent',
    section: 'custom_tools',
    default: null,
    placeholder: '{\n  "modules": ["tools.astrology"]\n}',
    displayMode: 'list',
    hasToolBrowser: true,
    readonly: true,
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value) return { valid: true };
          try {
            const parsed = JSON.parse(value);
            return validateToolsConfig(parsed);
          } catch (e: any) {
            return { valid: false, message: `Invalid JSON: ${e.message}` };
          }
        },
      },
    ],
  },

  // ========== Builtin Tools ==========
  builtin_tools: {
    type: FieldType.JSON,
    label: 'Builtin Tools Configuration',
    help: 'Enable/disable builtin tools (web search, calculator, etc.)',
    section: 'builtin_tools',
    default: null,
    placeholder: '{\n  "web_search": true\n}',
    displayMode: 'list',
    hasToolBrowser: true,
    readonly: true,
  },

  // ========== MCP Servers ==========
  mcp_servers: {
    type: FieldType.JSON,
    label: 'MCP Servers Configuration',
    help: 'Configure Model Context Protocol servers for tool access',
    section: 'mcp_tools',
    default: null,
    placeholder: '["astro-tool"]',
    displayMode: 'list',
    hasToolBrowser: true,
    readonly: true,
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value) return { valid: true };
          try {
            const parsed = JSON.parse(value);
            return validateMcpServersConfig(parsed);
          } catch (e: any) {
            return { valid: false, message: `Invalid JSON: ${e.message}` };
          }
        },
      },
    ],
  },

  // ========== Team Configuration ==========
  agent_mode: {
    type: FieldType.SELECT,
    label: 'Agent Mode',
    help: 'Solo agent or team leader mode',
    section: 'team',
    options: [
      { value: 'SOLO', label: 'Solo Agent' },
      { value: 'TEAM_LEADER', label: 'Team Leader' },
    ],
    default: 'SOLO',
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ['SOLO', 'TEAM_LEADER'] },
    ],
  },

  team_members: {
    type: FieldType.JSON,
    label: 'Team Members',
    help: 'Configure team member agents',
    section: 'team',
    default: null,
    placeholder: '["agent_type1", "agent_type2"]',
    showIf: (config) => config.agent_mode === 'TEAM_LEADER',
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value) return { valid: true };
          try {
            const parsed = JSON.parse(value);
            return validateTeamMembersConfig(parsed);
          } catch (e: any) {
            return { valid: false, message: `Invalid JSON: ${e.message}` };
          }
        },
      },
    ],
  },

  respond_directly: {
    type: FieldType.CHECKBOX,
    label: 'Respond Directly',
    help: 'Leader responds directly without delegating',
    section: 'team',
    default: false,
    showIf: (config) => config.agent_mode === 'TEAM_LEADER',
  },

  delegate_to_all_members: {
    type: FieldType.CHECKBOX,
    label: 'Delegate to All Members',
    help: 'Send task to all team members simultaneously',
    section: 'team',
    default: false,
    showIf: (config) => config.agent_mode === 'TEAM_LEADER',
  },

  share_member_interactions: {
    type: FieldType.CHECKBOX,
    label: 'Share Member Interactions',
    help: 'Share interactions between team members',
    section: 'team',
    default: false,
    showIf: (config) => config.agent_mode === 'TEAM_LEADER',
  },

  show_members_responses: {
    type: FieldType.CHECKBOX,
    label: 'Show Member Responses',
    help: 'Display individual member responses to user',
    section: 'team',
    default: false,
    showIf: (config) => config.agent_mode === 'TEAM_LEADER',
  },

  add_team_history_to_members: {
    type: FieldType.CHECKBOX,
    label: 'Add Team History to Members',
    help: 'Include team conversation history for each member',
    section: 'team',
    default: false,
    showIf: (config) => config.agent_mode === 'TEAM_LEADER',
  },

  determine_input_for_members: {
    type: FieldType.CHECKBOX,
    label: 'Determine Input for Members',
    help: 'Leader determines specific input for each member',
    section: 'team',
    default: false,
    showIf: (config) => config.agent_mode === 'TEAM_LEADER',
  },

  // ========== Memory Configuration ==========
  'memory.enabled': {
    type: FieldType.CHECKBOX,
    label: 'Enable Memory',
    help: 'Enable long-term memory storage',
    section: 'memory',
    default: true,
  },

  'memory.provider': {
    type: FieldType.SELECT,
    label: 'Memory Provider',
    help: 'Choose memory storage backend',
    section: 'memory',
    options: [
      { value: 'MEM0', label: 'MEM0' },
      { value: 'POSTGRES', label: 'PostgreSQL' },
      { value: 'NONE', label: 'None' },
    ],
    default: 'MEM0',
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ['MEM0', 'POSTGRES', 'NONE'] },
    ],
    invalidMessage: (value) => `Warning: Provider '${value}' is not valid. Expected: MEM0, POSTGRES, or NONE`,
  },

  'memory.model_id': {
    type: FieldType.SELECT,
    label: 'Memory Model',
    help: 'Model used for memory extraction and operations',
    section: 'memory',
    default: 'gpt-4o-mini',
    optionsSource: 'memoryModels',
    validation: [
      {
        rule: ValidationRuleType.IN_OPTIONS,
        getOptions: (app) => app.memoryModels?.map((m: any) => m.id) || [],
      },
    ],
    invalidMessage: (value) => `Warning: Model ID '${value}' is not in the available memory models list`,
  },

  'memory.enable_agentic_memory': {
    type: FieldType.CHECKBOX,
    label: 'Agentic Memory',
    help: 'Enable agentic memory management - adds memory control tools to the agent. For MEM0: adds search/add/update/delete memory tools. For PostgreSQL: adds memory search/save tools. When enabled, the agent can actively manage its own memories.',
    section: 'memory',
    default: true,
  },

  // ========== Memory Model Sampling ==========
  'memory.model_sampling.temperature': {
    type: FieldType.NUMBER,
    label: 'Memory Temperature',
    help: 'Memory model temperature (lower=more factual). Default: 0.3 (more deterministic than chat)',
    section: 'memory_sampling',
    min: 0.0,
    max: 2.0,
    step: 0.1,
    default: 0.3,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.0 },
      { rule: ValidationRuleType.MAX, value: 2.0 },
    ],
  },

  'memory.model_sampling.top_p': {
    type: FieldType.NUMBER,
    label: 'Memory Top P',
    help: 'Memory model nucleus sampling (default: 0.9 for precision)',
    section: 'memory_sampling',
    min: 0.0,
    max: 1.0,
    step: 0.1,
    default: 0.9,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.0 },
      { rule: ValidationRuleType.MAX, value: 1.0 },
    ],
  },

  'memory.model_sampling.max_tokens': {
    type: FieldType.NUMBER,
    label: 'Memory Max Tokens',
    help: 'Max tokens for memory operations (default: 2000)',
    placeholder: '2000',
    section: 'memory_sampling',
    min: 1,
    max: 128000,
    default: 2000,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 128000 },
    ],
  },

  // ========== Memory Custom Prompts ==========
  'memory.prompts.enable_custom_prompts': {
    type: FieldType.CHECKBOX,
    label: 'Enable Custom Prompts',
    help: 'Enable Mem0 custom prompts feature for this agent (Dual Prompt Architecture: User + Agent)',
    section: 'memory_prompts',
    default: false,
    showIf: (config) => getConfigValue(config, 'memory.provider') === 'MEM0',
  },

  'memory.prompts.user_memory_extraction_mode': {
    type: FieldType.SELECT,
    label: 'User Memory Extraction Mode',
    help: 'Choose to use system default or custom prompt for extracting memories from USER messages',
    section: 'memory_prompts',
    options: [
      { value: 'system', label: 'Use System Default' },
      { value: 'custom', label: 'Use Custom Prompt' },
    ],
    default: 'system',
    showIf: (config) =>
      getConfigValue(config, 'memory.prompts.enable_custom_prompts') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0',
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ['system', 'custom'] },
    ],
  },

  'memory.prompts.user_memory_extraction_prompt': {
    type: FieldType.TEXTAREA,
    label: 'Custom User Memory Extraction Prompt',
    help: 'Custom prompt for extracting memories from USER messages (preferences, facts, plans). Click "Reset to System Default" to load the default value.',
    placeholder: 'Enter custom user memory extraction prompt (50-4000 characters)...',
    section: 'memory_prompts',
    rows: 10,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, 'memory.prompts.enable_custom_prompts') === true &&
      getConfigValue(config, 'memory.prompts.user_memory_extraction_mode') === 'custom' &&
      getConfigValue(config, 'memory.provider') === 'MEM0',
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          if (value.length < 50) {
            return { valid: false, message: 'User memory extraction prompt must be at least 50 characters if provided' };
          }
          if (value.length > 4000) {
            return { valid: false, message: 'User memory extraction prompt cannot exceed 4000 characters' };
          }
          return { valid: true };
        },
      },
    ],
  },

  'memory.prompts.agent_memory_extraction_mode': {
    type: FieldType.SELECT,
    label: 'Agent Memory Extraction Mode',
    help: 'Choose to use system default or custom prompt for extracting memories from ASSISTANT messages',
    section: 'memory_prompts',
    options: [
      { value: 'system', label: 'Use System Default' },
      { value: 'custom', label: 'Use Custom Prompt' },
    ],
    default: 'system',
    showIf: (config) =>
      getConfigValue(config, 'memory.prompts.enable_custom_prompts') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0',
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ['system', 'custom'] },
    ],
  },

  'memory.prompts.agent_memory_extraction_prompt': {
    type: FieldType.TEXTAREA,
    label: 'Custom Agent Memory Extraction Prompt',
    help: 'Custom prompt for extracting memories from ASSISTANT messages (recommendations, warnings, context). Click "Reset to System Default" to load the default value.',
    placeholder: 'Enter custom agent memory extraction prompt (50-4000 characters)...',
    section: 'memory_prompts',
    rows: 10,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, 'memory.prompts.enable_custom_prompts') === true &&
      getConfigValue(config, 'memory.prompts.agent_memory_extraction_mode') === 'custom' &&
      getConfigValue(config, 'memory.provider') === 'MEM0',
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          if (value.length < 50) {
            return { valid: false, message: 'Agent memory extraction prompt must be at least 50 characters if provided' };
          }
          if (value.length > 4000) {
            return { valid: false, message: 'Agent memory extraction prompt cannot exceed 4000 characters' };
          }
          return { valid: true };
        },
      },
    ],
  },

  'memory.prompts.memory_update_mode': {
    type: FieldType.SELECT,
    label: 'Memory Update Mode',
    help: 'Choose to use system default or custom prompt for updating/merging memories',
    section: 'memory_prompts',
    options: [
      { value: 'system', label: 'Use System Default' },
      { value: 'custom', label: 'Use Custom Prompt' },
    ],
    default: 'system',
    showIf: (config) =>
      getConfigValue(config, 'memory.prompts.enable_custom_prompts') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0',
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ['system', 'custom'] },
    ],
  },

  'memory.prompts.memory_update_prompt': {
    type: FieldType.TEXTAREA,
    label: 'Custom Memory Update Prompt',
    help: 'Custom prompt for updating and merging memories. Click "Reset to System Default" to load the default value.',
    placeholder: 'Enter custom memory update prompt (50-4000 characters)...',
    section: 'memory_prompts',
    rows: 10,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, 'memory.prompts.enable_custom_prompts') === true &&
      getConfigValue(config, 'memory.prompts.memory_update_mode') === 'custom' &&
      getConfigValue(config, 'memory.provider') === 'MEM0',
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          if (value.length < 50) {
            return { valid: false, message: 'Memory update prompt must be at least 50 characters if provided' };
          }
          if (value.length > 4000) {
            return { valid: false, message: 'Memory update prompt cannot exceed 4000 characters' };
          }
          return { valid: true };
        },
      },
    ],
  },

  // ========== User Memory Categories ==========
  'memory.categories.enable_user_custom_categories': {
    type: FieldType.CHECKBOX,
    label: 'Enable User Custom Categories',
    help: 'Enable custom memory categories for USER memories (preferences, facts, plans)',
    section: 'memory_prompts',
    default: false,
    showIf: (config) => getConfigValue(config, 'memory.provider') === 'MEM0',
  },

  'memory.categories.user_categories_mode': {
    type: FieldType.SELECT,
    label: 'User Categories Mode',
    help: 'Choose to use system default or custom categories for user memories',
    section: 'memory_prompts',
    options: [
      { value: 'system', label: 'Use System Default' },
      { value: 'custom', label: 'Use Custom Categories' },
    ],
    default: 'system',
    showIf: (config) =>
      getConfigValue(config, 'memory.categories.enable_user_custom_categories') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0',
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ['system', 'custom'] },
    ],
  },

  'memory.categories.user_custom_categories': {
    type: FieldType.TEXTAREA,
    label: 'User Custom Categories (JSON)',
    help: 'Define custom USER memory categories in JSON format. Each category must have: description (string), examples (array), temporal (boolean). Click "Reset to System Default" to load the default categories.',
    placeholder: '{\n  "preference": {\n    "description": "User preferences",\n    "examples": ["likes pizza", "dislikes sweets"],\n    "temporal": false\n  }\n}',
    section: 'memory_prompts',
    rows: 15,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, 'memory.categories.enable_user_custom_categories') === true &&
      getConfigValue(config, 'memory.categories.user_categories_mode') === 'custom' &&
      getConfigValue(config, 'memory.provider') === 'MEM0',
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          try {
            const categories = JSON.parse(value);
            if (typeof categories !== 'object' || categories === null || Array.isArray(categories)) {
              return { valid: false, message: 'User custom categories must be a JSON object' };
            }
            for (const [catName, catConfig] of Object.entries(categories)) {
              const cat = catConfig as any;
              if (typeof cat !== 'object' || cat === null) {
                return { valid: false, message: `Category '${catName}' must be an object` };
              }
              if (!cat.description || typeof cat.description !== 'string') {
                return { valid: false, message: `Category '${catName}' must have a 'description' string field` };
              }
              if (!Array.isArray(cat.examples)) {
                return { valid: false, message: `Category '${catName}' must have an 'examples' array field` };
              }
              if (typeof cat.temporal !== 'boolean') {
                return { valid: false, message: `Category '${catName}' must have a 'temporal' boolean field` };
              }
            }
            return { valid: true };
          } catch (e: any) {
            return { valid: false, message: `Invalid JSON format: ${e.message}` };
          }
        },
      },
    ],
  },

  // ========== Agent Memory Categories ==========
  'memory.categories.enable_agent_custom_categories': {
    type: FieldType.CHECKBOX,
    label: 'Enable Agent Custom Categories',
    help: 'Enable custom memory categories for AGENT memories (recommendations, warnings, context)',
    section: 'memory_prompts',
    default: false,
    showIf: (config) => getConfigValue(config, 'memory.provider') === 'MEM0',
  },

  'memory.categories.agent_categories_mode': {
    type: FieldType.SELECT,
    label: 'Agent Categories Mode',
    help: 'Choose to use system default or custom categories for agent memories',
    section: 'memory_prompts',
    options: [
      { value: 'system', label: 'Use System Default' },
      { value: 'custom', label: 'Use Custom Categories' },
    ],
    default: 'system',
    showIf: (config) =>
      getConfigValue(config, 'memory.categories.enable_agent_custom_categories') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0',
    validation: [
      { rule: ValidationRuleType.IN_OPTIONS, options: ['system', 'custom'] },
    ],
  },

  'memory.categories.agent_custom_categories': {
    type: FieldType.TEXTAREA,
    label: 'Agent Custom Categories (JSON)',
    help: 'Define custom AGENT memory categories in JSON format. Each category must have: description (string), examples (array), temporal (boolean). Click "Reset to System Default" to load the default categories.',
    placeholder: '{\n  "recommendation": {\n    "description": "Recommendations",\n    "examples": ["exercise daily", "learn Python"],\n    "temporal": false\n  }\n}',
    section: 'memory_prompts',
    rows: 15,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, 'memory.categories.enable_agent_custom_categories') === true &&
      getConfigValue(config, 'memory.categories.agent_categories_mode') === 'custom' &&
      getConfigValue(config, 'memory.provider') === 'MEM0',
    validation: [
      {
        rule: ValidationRuleType.CUSTOM,
        validate: (value) => {
          if (!value || !value.trim()) return { valid: true };
          try {
            const categories = JSON.parse(value);
            if (typeof categories !== 'object' || categories === null || Array.isArray(categories)) {
              return { valid: false, message: 'Agent custom categories must be a JSON object' };
            }
            for (const [catName, catConfig] of Object.entries(categories)) {
              const cat = catConfig as any;
              if (typeof cat !== 'object' || cat === null) {
                return { valid: false, message: `Category '${catName}' must be an object` };
              }
              if (!cat.description || typeof cat.description !== 'string') {
                return { valid: false, message: `Category '${catName}' must have a 'description' string field` };
              }
              if (!Array.isArray(cat.examples)) {
                return { valid: false, message: `Category '${catName}' must have an 'examples' array field` };
              }
              if (typeof cat.temporal !== 'boolean') {
                return { valid: false, message: `Category '${catName}' must have a 'temporal' boolean field` };
              }
            }
            return { valid: true };
          } catch (e: any) {
            return { valid: false, message: `Invalid JSON format: ${e.message}` };
          }
        },
      },
    ],
  },

  // ========== Memory Query Configuration (hooks.user_facts, hooks.memory_context) ==========
  // Fields show only when their corresponding hook is enabled in the Hooks Pipeline
  // Note: memory_context hook now handles both user_events AND agent_memories (unified in single hook)
  'hooks.user_facts.facts_limit': {
    type: FieldType.NUMBER,
    label: 'Facts Query Limit',
    help: 'Maximum number of permanent user facts to retrieve (1-100)',
    section: 'memory_query',
    min: 1,
    max: 100,
    default: 20,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.user_facts') !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 100 },
    ],
  },

  'hooks.memory_context.events_limit': {
    type: FieldType.NUMBER,
    label: 'Events Query Limit',
    help: 'Maximum number of temporal event memories to retrieve (1-50)',
    section: 'memory_query',
    min: 1,
    max: 50,
    default: 10,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.memory_context') !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 50 },
    ],
  },

  'hooks.memory_context.events_threshold': {
    type: FieldType.NUMBER,
    label: 'Events Search Threshold',
    help: 'Similarity threshold for event search (0.0-1.0). Lower values return more results.',
    section: 'memory_query',
    min: 0.0,
    max: 1.0,
    step: 0.1,
    default: 0.5,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.memory_context') !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.0 },
      { rule: ValidationRuleType.MAX, value: 1.0 },
    ],
  },

  // Agent memories config - now part of unified memory_context hook
  'hooks.memory_context.agent_memories_limit': {
    type: FieldType.NUMBER,
    label: 'Agent Memories Limit',
    help: 'Maximum number of agent-specific memories to retrieve (1-20)',
    section: 'memory_query',
    min: 1,
    max: 20,
    default: 5,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.memory_context') !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1 },
      { rule: ValidationRuleType.MAX, value: 20 },
    ],
  },

  'hooks.memory_context.agent_memories_threshold': {
    type: FieldType.NUMBER,
    label: 'Agent Memories Threshold',
    help: 'Similarity threshold for agent memory search (0.0-1.0). Lower values return more results.',
    section: 'memory_query',
    min: 0.0,
    max: 1.0,
    step: 0.1,
    default: 0.5,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.memory_context') !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.0 },
      { rule: ValidationRuleType.MAX, value: 1.0 },
    ],
  },

  // Shared query settings - show if ANY memory hook is visible
  'hooks.memory_context.query_max_length': {
    type: FieldType.NUMBER,
    label: 'Query Max Length',
    help: 'Maximum total length of memory search query in characters (100-1000)',
    section: 'memory_query',
    min: 100,
    max: 1000,
    step: 50,
    default: 500,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      (getConfigValue(config, 'hooks_visible.user_facts') !== false ||
        getConfigValue(config, 'hooks_visible.memory_context') !== false),
    validation: [
      { rule: ValidationRuleType.MIN, value: 100 },
      { rule: ValidationRuleType.MAX, value: 1000 },
    ],
  },

  'hooks.memory_context.user_query_length': {
    type: FieldType.NUMBER,
    label: 'User Query Length',
    help: 'Max length of current user question in search query (50-500 chars)',
    section: 'memory_query',
    min: 50,
    max: 500,
    step: 50,
    default: 250,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      (getConfigValue(config, 'hooks_visible.user_facts') !== false ||
        getConfigValue(config, 'hooks_visible.memory_context') !== false),
    validation: [
      { rule: ValidationRuleType.MIN, value: 50 },
      { rule: ValidationRuleType.MAX, value: 500 },
    ],
  },

  'hooks.memory_context.assistant_context_length': {
    type: FieldType.NUMBER,
    label: 'Assistant Context Length',
    help: 'Max length of last assistant response in search query (0-500 chars). Set to 0 to disable assistant context.',
    section: 'memory_query',
    min: 0,
    max: 500,
    step: 50,
    default: 200,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      (getConfigValue(config, 'hooks_visible.user_facts') !== false ||
        getConfigValue(config, 'hooks_visible.memory_context') !== false),
    validation: [
      { rule: ValidationRuleType.MIN, value: 0 },
      { rule: ValidationRuleType.MAX, value: 500 },
    ],
  },

  // ========== Memory Decay (hooks.memory_reinforce) ==========
  'hooks.memory_reinforce.enabled': {
    type: FieldType.CHECKBOX,
    label: 'Enable Memory Decay',
    help: 'Enable Ebbinghaus forgetting curve for temporal memories. Memories decay over time based on access frequency.',
    section: 'memory_ebbinghaus',
    default: false,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.memory_reinforce') !== false,
  },

  'hooks.memory_reinforce.decay_factor': {
    type: FieldType.NUMBER,
    label: 'Decay Factor',
    help: 'Controls decay speed (1.0=fast forget, 30.0=slow forget). Higher values mean slower memory decay.',
    section: 'memory_ebbinghaus',
    min: 1.0,
    max: 30.0,
    step: 1.0,
    default: 7.0,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.memory_reinforce') !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1.0 },
      { rule: ValidationRuleType.MAX, value: 30.0 },
    ],
  },

  'hooks.memory_reinforce.min_retention': {
    type: FieldType.NUMBER,
    label: 'Min Retention',
    help: 'Minimum retention floor (0.0-0.5). Memories never decay below this value. Default 0.1 means memories retain at least 10% relevance.',
    section: 'memory_ebbinghaus',
    min: 0.0,
    max: 0.5,
    step: 0.05,
    default: 0.1,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.memory_reinforce') !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.0 },
      { rule: ValidationRuleType.MAX, value: 0.5 },
    ],
  },

  'hooks.memory_reinforce.initial_strength': {
    type: FieldType.NUMBER,
    label: 'Initial Strength',
    help: 'Starting memory strength for new memories (0.5-2.0). Higher values make new memories more resistant to decay.',
    section: 'memory_ebbinghaus',
    min: 0.5,
    max: 2.0,
    step: 0.1,
    default: 1.0,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.memory_reinforce') !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.5 },
      { rule: ValidationRuleType.MAX, value: 2.0 },
    ],
  },

  'hooks.memory_reinforce.reinforcement_boost': {
    type: FieldType.NUMBER,
    label: 'Reinforcement Boost',
    help: 'Strength increase when memory is accessed (0.1-1.0). Higher values strengthen memories more on each access.',
    section: 'memory_ebbinghaus',
    min: 0.1,
    max: 1.0,
    step: 0.1,
    default: 0.3,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.memory_reinforce') !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 0.1 },
      { rule: ValidationRuleType.MAX, value: 1.0 },
    ],
  },

  'hooks.memory_reinforce.max_strength': {
    type: FieldType.NUMBER,
    label: 'Max Strength',
    help: 'Maximum memory strength cap (1.0-5.0). Limits how strong a memory can become through reinforcement.',
    section: 'memory_ebbinghaus',
    min: 1.0,
    max: 5.0,
    step: 0.5,
    default: 3.0,
    showIf: (config) =>
      getConfigValue(config, 'memory.enabled') === true &&
      getConfigValue(config, 'memory.provider') === 'MEM0' &&
      getConfigValue(config, 'hooks_visible.memory_reinforce') !== false,
    validation: [
      { rule: ValidationRuleType.MIN, value: 1.0 },
      { rule: ValidationRuleType.MAX, value: 5.0 },
    ],
  },

  // ========== System Prompts ==========
  instructions: {
    type: FieldType.MARKDOWN,
    label: 'System Instructions',
    placeholder: 'Enter system instructions in Markdown...',
    help: 'Main system prompt that defines agent personality and behavior',
    section: 'prompts',
    rows: 15,
    default: '',
  },

  additional_context: {
    type: FieldType.MARKDOWN,
    label: 'Additional Context',
    placeholder: 'Enter additional context in Markdown...',
    help: 'Extra context appended to system instructions',
    section: 'prompts',
    rows: 8,
    default: null,
  },

  // ========== First Message Hook (hooks.first_message) ==========
  'hooks.first_message.enabled': {
    type: FieldType.CHECKBOX,
    label: 'Enable First Message Hook',
    help: 'Add special instruction for first message in new sessions',
    section: 'first_message',
    default: true,
    showIf: (config) => getConfigValue(config, 'hooks_visible.first_message') !== false,
  },

  'hooks.first_message.mode': {
    type: FieldType.SELECT,
    label: 'First Message Mode',
    help: 'Use system default or custom instruction',
    section: 'first_message',
    options: [
      { value: 'system', label: 'Use System Default' },
      { value: 'custom', label: 'Use Custom Instruction' },
    ],
    default: 'system',
    showIf: (config) => getConfigValue(config, 'hooks_visible.first_message') !== false,
  },

  'hooks.first_message.custom_instruction': {
    type: FieldType.TEXTAREA,
    label: 'Custom First Message Instruction',
    placeholder: 'Enter custom instruction for first message...',
    help: 'Custom instruction shown only in first message of new sessions',
    section: 'first_message',
    rows: 5,
    default: null,
    hasResetButton: true,
    showIf: (config) =>
      getConfigValue(config, 'hooks_visible.first_message') !== false &&
      getConfigValue(config, 'hooks.first_message.mode') === 'custom',
  },

  // ========== Response Classifier ==========
  // Binary classification for quick vs deep responses
  // Note: enabled, model_id, llm_timeout moved to hooks.classifier

  'hooks.classifier.enabled': {
    type: FieldType.CHECKBOX,
    label: 'Enable Response Classifier',
    help: 'Classify user messages to determine response type (quick vs deep). Enables dynamic prompt selection based on message complexity.',
    section: 'response_classifier',
    default: false,
    showIf: (config) => getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  'hooks.classifier.model_id': {
    type: FieldType.SELECT,
    label: 'Classification Model',
    help: 'Model used for LLM-based classification when rules are uncertain. Use a fast, cost-effective model.',
    section: 'response_classifier',
    default: 'gpt-5-mini',
    optionsSource: 'memoryModels',
    showIf: (config) =>
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  'hooks.classifier.rule_confidence_threshold': {
    type: FieldType.NUMBER,
    label: 'Rule Confidence Threshold',
    help: 'Confidence threshold for rule-based classification. Below this value, LLM classification is used. Range: 0.0-1.0, default: 0.8',
    section: 'response_classifier',
    min: 0.0,
    max: 1.0,
    step: 0.05,
    default: 0.8,
    showIf: (config) =>
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  'hooks.classifier.quick_response_max_length': {
    type: FieldType.NUMBER,
    label: 'Quick Response Max Length',
    help: 'Maximum message length (characters) for quick response classification. Longer messages tend toward deep responses.',
    section: 'response_classifier',
    min: 10,
    max: 500,
    step: 10,
    default: 50,
    showIf: (config) =>
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  'hooks.classifier.llm_timeout': {
    type: FieldType.NUMBER,
    label: 'LLM Timeout (seconds)',
    help: 'Timeout for LLM classification in seconds. If exceeded, defaults to deep response.',
    section: 'response_classifier',
    min: 1,
    max: 60,
    step: 1,
    default: 30,
    showIf: (config) =>
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  // ========== Quick Response Model Override ==========

  'hooks.classifier.quick_response_model.enabled': {
    type: FieldType.CHECKBOX,
    label: '启用 QUICK 模型覆盖',
    help: '为 QUICK 类型响应使用独立的模型配置。可使用更快/更便宜的模型处理简单查询。',
    section: 'response_classifier',
    default: false,
    showIf: (config) =>
      getConfigValue(config, 'hooks.classifier.enabled') === true &&
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  'hooks.classifier.quick_response_model.model_id': {
    type: FieldType.SELECT,
    label: 'QUICK 响应模型',
    help: '被分类为 QUICK 时使用的模型。建议使用快速、低成本的模型。',
    section: 'response_classifier',
    default: 'gpt-5-mini',
    optionsSource: 'chatModels',
    showIf: (config) =>
      getConfigValue(config, 'hooks.classifier.enabled') === true &&
      getConfigValue(config, 'hooks.classifier.quick_response_model.enabled') === true &&
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  'hooks.classifier.quick_response_model.temperature': {
    type: FieldType.NUMBER,
    label: 'QUICK 模型 Temperature',
    help: 'QUICK 响应的采样温度 (0=确定性, 越高越随机)',
    section: 'response_classifier',
    min: 0.0,
    max: 2.0,
    step: 0.1,
    default: 0.7,
    showIf: (config) =>
      getConfigValue(config, 'hooks.classifier.enabled') === true &&
      getConfigValue(config, 'hooks.classifier.quick_response_model.enabled') === true &&
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  'hooks.classifier.quick_response_model.top_p': {
    type: FieldType.NUMBER,
    label: 'QUICK 模型 Top P',
    help: 'QUICK 响应的核采样概率。越低输出越聚焦。',
    section: 'response_classifier',
    min: 0.0,
    max: 1.0,
    step: 0.05,
    default: 1.0,
    showIf: (config) =>
      getConfigValue(config, 'hooks.classifier.enabled') === true &&
      getConfigValue(config, 'hooks.classifier.quick_response_model.enabled') === true &&
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  'hooks.classifier.quick_response_model.max_tokens': {
    type: FieldType.NUMBER,
    label: 'QUICK 模型 Max Tokens',
    help: 'QUICK 响应的最大 token 数。较小值 = 更快响应。',
    section: 'response_classifier',
    min: 1,
    max: 128000,
    step: 100,
    default: 500,
    showIf: (config) =>
      getConfigValue(config, 'hooks.classifier.enabled') === true &&
      getConfigValue(config, 'hooks.classifier.quick_response_model.enabled') === true &&
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  // ========== Response Type Instructions ==========

  'hooks.classifier.quick_response_instructions': {
    type: FieldType.TEXTAREA,
    label: 'Quick Response Instructions',
    help: 'Instructions used for quick responses (greetings, confirmations, casual chat). If empty, default instructions will be used instead.',
    placeholder: '你是一个友好的助手。请简洁、自然地回复用户的问候或简单问题。保持轻松愉快的对话氛围。',
    section: 'response_classifier_prompts',
    rows: 8,
    default: '',
    showIf: (config) =>
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  'hooks.classifier.deep_response_instructions': {
    type: FieldType.TEXTAREA,
    label: 'Deep Response Instructions',
    help: 'Instructions used for deep responses (analysis, consultation, emotional support). If empty, default instructions will be used instead.',
    placeholder: '你是一位专业的顾问。请认真分析用户的问题，提供深入、专业的见解和建议。考虑多个角度，给出有价值的分析。',
    section: 'response_classifier_prompts',
    rows: 8,
    default: '',
    showIf: (config) =>
      getConfigValue(config, 'hooks_visible.classifier') !== false,
  },

  // Skills configuration is now managed directly in the Skills tab (SkillsTab.tsx)
  // Fields: skills_enabled, semantic_matching_enabled, semantic_similarity_threshold
};

export default FIELD_CONFIG;
